import type { Vec2, World } from '../world/types.js';

/** Euclidean distance between two points. */
export function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Smallest signed delta between two angles, in [-π, π]. */
export function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Circle vs world wall AABB list. Returns true on overlap. */
export function collidesWalls(world: World, x: number, y: number, r: number): boolean {
  for (const w of world.walls) {
    const cx = Math.max(w.x, Math.min(x, w.x + w.w));
    const cy = Math.max(w.y, Math.min(y, w.y + w.h));
    if (Math.hypot(x - cx, y - cy) < r) return true;
  }
  return false;
}

export interface RayHit {
  x: number;
  y: number;
  d: number;
}

/**
 * 4 px stepped ray-cast against the world's walls. Returns the first wall
 * hit point, or null if the segment is clear.
 */
export function raycastHit(
  world: World,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): RayHit | null {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const d = Math.hypot(dx, dy);
  const steps = Math.max(1, Math.ceil(d / 4));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const x = x1 + dx * t;
    const y = y1 + dy * t;
    for (const w of world.walls) {
      if (x >= w.x && x <= w.x + w.w && y >= w.y && y <= w.y + w.h) {
        return { x, y, d: d * t };
      }
    }
  }
  return null;
}

/** True if any light source can reach `p` without a wall in between. */
export function inLight(world: World, p: Vec2): boolean {
  for (const L of world.lights) {
    if (dist(p, L) < L.r) {
      if (!raycastHit(world, L.x, L.y, p.x, p.y)) return true;
    }
  }
  return false;
}
